import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Check, Zap, Loader2, X, AlertTriangle,
} from 'lucide-react'
import { InlineWebhookPanel } from './components/InlineWebhookPanel'
import {
  inputCls, labelCls, SectionLabel, CopyButton, RawJsonCollapsible, ComboSelect,
  EndpointPicker, PayloadEditor, CurlCollapsible,
} from './components/shared'
import type { EndpointDef } from './components/shared'
import { HOSTED_FLOWS, DEFAULT_CREDS } from './data'
import { proxyUrl, buildHeaders, outcomeColors } from './utils'
import type { Creds, LogEntry } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = Record<string, any>

const HOSTED_V2_ENDPOINTS: EndpointDef[] = [
  { id: 'generate-url', method: 'POST', label: 'Generate URL', path: '/individuals/hostedUrl', category: 'Hosted', core: true },
  { id: 'get-individual', method: 'GET', label: 'Get Individual', path: '/individuals/{entityId}', category: 'Entity Management', core: true },
  // IDV
  { id: 'idv-process', method: 'POST', label: 'Process IDV Results', path: '/individuals/{entityId}/actions/idv/process', category: 'IDV' },
  // Results
  { id: 'get-results', method: 'GET', label: 'Get Process Results', path: '/individuals/{entityId}/results', category: 'Results' },
  // Entity Management
  { id: 'update-entity', method: 'PATCH', label: 'Update Individual', path: '/individuals/{entityId}', category: 'Entity Management' },
  { id: 'delete-entity', method: 'DELETE', label: 'Delete Individual', path: '/individuals/{entityId}', category: 'Entity Management' },
]

// ── Main component ─────────────────────────────────────────────────────────────

interface HostedV2WorkspaceProps {
  creds: Creds
  addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void
}

export function HostedV2Workspace({ creds, addLog }: HostedV2WorkspaceProps) {
  const apiBase = creds.apiBase || DEFAULT_CREDS.apiBase
  const hasCreds = !!(creds.customerId && creds.apiKey)

  const [selectedEndpoint, setSelectedEndpoint] = useState<string>('generate-url')
  const [flowId, setFlowId] = useState('idv_daon')
  const [redirectUrl, setRedirectUrl] = useState('https://your-app.com/kyc/complete')
  const [existingEntityId, setExistingEntityId] = useState('')
  const [sendSms, setSendSms] = useState(false)
  const [phoneCode, setPhoneCode] = useState('+61')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [getEntityIdInput, setGetEntityIdInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [resultLoading, setResultLoading] = useState(false)
  const [hostedUrl, setHostedUrl] = useState<string | null>(null)
  const [entityId, setEntityId] = useState<string | null>(null)
  const [events, setEvents] = useState<{ ts: string; [k: string]: unknown }[]>([])
  const [workflowResult, setWorkflowResult] = useState<AnyObj | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)
  const autoFetchedRef = useRef(false)

  // PayloadEditor state
  const [editorText, setEditorText] = useState('')
  const [editorDirty, setEditorDirty] = useState(false)
  const [editorError, setEditorError] = useState<string | null>(null)

  const epDef = HOSTED_V2_ENDPOINTS.find(e => e.id === selectedEndpoint) ?? HOSTED_V2_ENDPOINTS[0]
  const isGetEndpoint = epDef.method === 'GET'

  function derivedPayload(): Record<string, unknown> {
    const body: Record<string, unknown> = { consentObtained: true, oneSDKFlowId: flowId, redirectUrl }
    if (existingEntityId.trim()) body.entityId = existingEntityId.trim()
    if (sendSms && phoneNumber.trim()) {
      body.sendSMS = true; body.phoneNumber = phoneNumber.trim(); body.phoneCode = phoneCode
      if (recipientName.trim()) body.recipientName = recipientName.trim()
    }
    return body
  }

  function dynamicPath(): string {
    const eid = entityId ?? getEntityIdInput
    if (selectedEndpoint === 'get-individual') return `/individuals/${eid || '{entityId}'}`
    return '/individuals/hostedUrl'
  }

  const fullUrl = `${apiBase}${dynamicPath()}`

  // Sync derived payload to editor
  const derivedJson = JSON.stringify(derivedPayload())
  useEffect(() => {
    if (!editorDirty && selectedEndpoint === 'generate-url') {
      try {
        setEditorText(JSON.stringify(JSON.parse(derivedJson), null, 2))
      } catch {
        setEditorText(derivedJson)
      }
      setEditorError(null)
    }
  }, [derivedJson, editorDirty, selectedEndpoint])

  // Auto-populate GET input from prior results
  useEffect(() => {
    if (entityId && !getEntityIdInput) setGetEntityIdInput(entityId)
  }, [entityId, getEntityIdInput])

  function handleEndpointSelect(id: string) {
    setSelectedEndpoint(id)
    setEditorDirty(false)
    setEditorError(null)
  }

  function handleEditorChange(v: string) { setEditorText(v); setEditorDirty(true); setEditorError(null) }
  function resetEditor() { setEditorDirty(false); setEditorText(JSON.stringify(derivedPayload(), null, 2)); setEditorError(null) }
  function getEditorPayload(): Record<string, unknown> | null {
    try { return JSON.parse(editorText) as Record<string, unknown> }
    catch { setEditorError('Invalid JSON — fix syntax before sending'); return null }
  }

  useEffect(() => {
    function handler(e: MessageEvent) {
      if (e.data?.['X-Frankie-Source'] === 'hosted-onesdk') {
        setEvents(prev => [...prev, { ts: new Date().toLocaleTimeString(), ...e.data as Record<string, unknown> }])
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [])

  const getResult = useCallback(async () => {
    const eid = entityId ?? getEntityIdInput
    if (!eid) return
    setResultLoading(true)
    const url = `${apiBase}/individuals/${eid}`
    const start = Date.now()
    try {
      const res = await fetch(proxyUrl(`/individuals/${eid}`), { method: 'GET', headers: buildHeaders(creds) })
      const durationMs = Date.now() - start
      const data = await res.json() as AnyObj
      addLog({ method: 'GET', url, status: res.status, durationMs, responseBody: data, annotation: 'Fetch individual record after hosted verification' })
      if (res.ok) {
        setFullResult(data)
        const wr = data?.workflowResult ?? data?.serviceProfiles?.[0]?.workflowSummaries?.[0] ?? null
        setWorkflowResult(wr as AnyObj | null)
      }
    } catch { /* logged */ } finally { setResultLoading(false) }
  }, [entityId, getEntityIdInput, apiBase, creds, addLog])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const last = events[events.length - 1]
    if (last?.type === 'SUCCESS' && entityId && !workflowResult && !autoFetchedRef.current) {
      autoFetchedRef.current = true
      void getResult()
    }
  }, [events])

  async function generateUrl() {
    const payload = getEditorPayload() ?? derivedPayload()
    setLoading(true); setHostedUrl(null); setEntityId(null); setWorkflowResult(null); setFullResult(null)
    const url = `${apiBase}/individuals/hostedUrl`
    const start = Date.now()
    try {
      const res = await fetch(proxyUrl('/individuals/hostedUrl'), { method: 'POST', headers: buildHeaders(creds, true), body: JSON.stringify(payload) })
      const durationMs = Date.now() - start
      const data = await res.json() as { entityId?: string; url?: string }
      addLog({ method: 'POST', url, status: res.status, durationMs, requestBody: payload, responseBody: data, annotation: 'Generate Hosted OneSDK URL — returns { entityId, url }' })
      if (res.ok && data?.url) { setHostedUrl(data.url); setEntityId(data.entityId ?? null); if (data.entityId) setGetEntityIdInput(data.entityId) }
    } catch { /* logged */ } finally { setLoading(false) }
  }

  function reset() {
    setHostedUrl(null); setEntityId(null); setEvents([]); setWorkflowResult(null)
    setFullResult(null); autoFetchedRef.current = false; setGetEntityIdInput('')
  }

  const outcome = workflowResult ? (() => {
    const wr = workflowResult
    const s = (wr.workflowExecutionState ?? wr.overallRiskLevel ?? '').toUpperCase()
    if (wr.issues?.length || s === 'TIMEOUT' || s === 'ERROR') return 'FAIL'
    const rs = (wr.checkSummary?.overallRiskLevel ?? wr.riskAssessment?.riskLevel ?? s)
    if (rs === 'LOW' || rs === 'CLEAR' || rs === 'PASS') return 'PASS'
    if (rs === 'MEDIUM' || rs === 'REFER' || rs === 'REVIEW') return 'REFER'
    if (rs === 'HIGH' || rs === 'FAIL') return 'FAIL'
    return s || 'DONE'
  })() : ''
  const oc = outcomeColors(outcome)

  const curlBody = isGetEndpoint ? undefined : (editorDirty ? (getEditorPayload() ?? undefined) : derivedPayload())

  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: '200px minmax(350px, 400px) 1fr', alignItems: 'start', padding: '18px 0' }}
    >
      {/* ── COLUMN 1: Endpoint Picker ── */}
      <EndpointPicker
        endpoints={HOSTED_V2_ENDPOINTS}
        selectedId={selectedEndpoint}
        onSelect={handleEndpointSelect}
      />

      {/* ── COLUMN 2: Form Panel ── */}
      <div className="rounded-[14px] overflow-hidden" style={{ background: '#111c30', border: '1px solid #1e2c44' }}>
        {/* Header strip */}
        <div className="flex items-center gap-2.5 px-4 py-3" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44' }}>
          <span
            className="px-2 py-0.5 rounded text-[10px] font-bold font-mono"
            style={isGetEndpoint
              ? { background: 'rgba(96,165,250,.15)', color: '#60a5fa' }
              : { background: 'rgba(52,211,153,.13)', color: '#34d399' }}
          >
            {epDef.method}
          </span>
          <span className="text-[11px] font-mono text-[#8aa0c2] truncate flex-1">{epDef.path}</span>
        </div>

        <div className="p-4 space-y-4">
          {/* generate-url form */}
          {selectedEndpoint === 'generate-url' && (
            <>
              <div>
                <label className={labelCls}>Flow ID <span style={{ color: '#f87171' }}>*</span></label>
                <ComboSelect
                  value={flowId}
                  onChange={setFlowId}
                  options={HOSTED_FLOWS.map(f => ({ value: f.id, label: f.label }))}
                  placeholder="e.g. idv, manual_kyc, or custom flow"
                  className={inputCls}
                />
              </div>

              <div>
                <label className={labelCls}>Redirect URL <span style={{ color: '#f87171' }}>*</span></label>
                <input value={redirectUrl} onChange={e => setRedirectUrl(e.target.value)} className={inputCls} placeholder="https://your-app.com/kyc/complete" />
              </div>

              <div>
                <label className={labelCls}>Entity ID <span className="normal-case font-normal text-[#5b6b85]">(optional)</span></label>
                <input value={existingEntityId} onChange={e => setExistingEntityId(e.target.value)} className={inputCls} placeholder="omit to create new" />
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input type="checkbox" checked={sendSms} onChange={e => setSendSms(e.target.checked)} style={{ accentColor: '#6366f1' }} />
                  <span className="text-[11px] text-[#94a3b8]">Send link via SMS</span>
                </label>
                {sendSms && (
                  <div className="space-y-2 pl-1">
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <label className={labelCls}>Code</label>
                        <input value={phoneCode} onChange={e => setPhoneCode(e.target.value)} className={inputCls} placeholder="+61" />
                      </div>
                      <div className="col-span-3">
                        <label className={labelCls}>Phone Number</label>
                        <input value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} className={inputCls} placeholder="0412345678" />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Recipient Name <span className="normal-case font-normal text-[#5b6b85]">(optional)</span></label>
                      <input value={recipientName} onChange={e => setRecipientName(e.target.value)} className={inputCls} placeholder="Jane Smith" />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-1">
                <button
                  onClick={() => void generateUrl()}
                  disabled={loading || !hasCreds || !flowId || !redirectUrl || (sendSms && !phoneNumber.trim())}
                  className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                  style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
                >
                  {loading
                    ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Generating…</span>
                    : 'Generate Hosted URL'
                  }
                </button>

                {(hostedUrl || entityId) && (
                  <button onClick={reset} className="w-full py-2 rounded-[8px] text-[12px] font-medium transition-colors"
                    style={{ background: '#16223a', border: '1px solid #283a57', color: '#c7d2fe' }}>
                    <X size={11} className="inline mr-1" />Reset
                  </button>
                )}

                {!hasCreds && <p className="text-[10px] text-amber-400 text-center">Configure credentials above to make live calls</p>}

                {entityId && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.2)' }}>
                    <span className="text-[10px] font-semibold text-emerald-400">entityId</span>
                    <span className="text-[10px] font-mono text-[#94a3b8] flex-1 truncate">{entityId}</span>
                    <CopyButton text={entityId} />
                  </div>
                )}

                {hostedUrl && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#5b6b85]">Hosted URL:</span>
                      <CopyButton text={hostedUrl} />
                    </div>
                    <a href={hostedUrl} target="_blank" rel="noreferrer" className="text-[10px] font-mono break-all hover:underline block" style={{ color: '#a78bfa' }}>{hostedUrl}</a>
                  </div>
                )}
              </div>
            </>
          )}

          {/* get-individual form */}
          {selectedEndpoint === 'get-individual' && (
            <>
              <div>
                <label className={labelCls}>Entity ID</label>
                <input
                  className={inputCls}
                  value={getEntityIdInput}
                  onChange={e => setGetEntityIdInput(e.target.value)}
                  placeholder="entityId from generate URL step"
                />
              </div>

              <div className="space-y-2 pt-1">
                {!hasCreds && <p className="text-[10px] text-amber-400 text-center">Configure credentials above to make live calls</p>}
                <button
                  onClick={() => void getResult()}
                  disabled={resultLoading || !hasCreds || !(entityId ?? getEntityIdInput)}
                  className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                  style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
                >
                  {resultLoading
                    ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Fetching…</span>
                    : 'Get Result'
                  }
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── COLUMN 3: Results — always-visible panels ── */}
      <div className="space-y-3 min-w-0">
        {/* PayloadEditor + cURL — above webhooks */}
        {!isGetEndpoint && (
          <PayloadEditor
            value={editorText}
            dirty={editorDirty}
            error={editorError}
            onChange={handleEditorChange}
            onReset={resetEditor}
          />
        )}
        <CurlCollapsible method={epDef.method} url={fullUrl} creds={creds} body={isGetEndpoint ? undefined : curlBody} />

        <InlineWebhookPanel entityId={entityId} customerId={creds.customerId || null} />

        {/* iframe */}
        <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
          <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: '1px solid #1e2c44', background: '#0a1322' }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">Hosted Verification iframe</p>
            <div className="flex items-center gap-3">
              {hostedUrl && <p className="text-[10px] text-[#5b6b85] font-mono">{new URL(hostedUrl).host}</p>}
              {hostedUrl && (
                <a href={hostedUrl} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors"
                  style={{ background: 'rgba(167,139,250,.15)', border: '1px solid rgba(167,139,250,.3)', color: '#a78bfa' }}>
                  ↗ Open in new tab
                </a>
              )}
            </div>
          </div>
          {hostedUrl ? (
            <iframe
              src={hostedUrl}
              allow="camera;fullscreen;accelerometer;gyroscope;magnetometer;geolocation;microphone;"
              className="w-full"
              style={{ minHeight: 620, border: 0 }}
              title="FrankieOne Hosted Verification"
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <Zap size={22} className="text-[#3f4f6b] mb-3" />
              <p className="text-[11px] text-[#475569] italic">Generate a hosted URL to load the iframe here</p>
            </div>
          )}
        </div>

        {/* postMessage events */}
        <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
          <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: '1px solid #1e2c44', background: '#0a1322' }}>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">postMessage Events</span>
              {events.length > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(167,139,250,.2)', color: '#a78bfa' }}>{events.length}</span>}
            </div>
            {events.length > 0 && <button onClick={() => setEvents([])} className="text-[10px] text-[#5b6b85] hover:text-red-400 transition-colors">Clear</button>}
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {events.length === 0 ? (
              <p className="text-[10px] text-[#475569] p-3 italic">{hostedUrl ? 'Iframe loaded — complete flow to see events' : 'No events yet'}</p>
            ) : events.map((ev, i) => (
              <div key={i} className="px-4 py-2 border-b" style={{ borderColor: '#1a2740' }}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-[9px] text-[#5b6b85]">{String(ev.ts)}</span>
                  {ev.type !== undefined && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-mono font-medium" style={{ background: 'rgba(167,139,250,.2)', color: '#a78bfa' }}>{String(ev.type)}</span>
                  )}
                </div>
                <pre className="text-[9px] text-[#94a3b8] font-mono overflow-auto">{JSON.stringify(ev, null, 2)}</pre>
              </div>
            ))}
          </div>
        </div>

        {/* Result */}
        {workflowResult && (
          <div className="space-y-3" style={{ animation: 'fadeUp .25s ease' }}>
            <div className="p-4 rounded-[14px]" style={{ background: oc.bg, border: `1px solid ${oc.border}` }}>
              <div className="flex items-center gap-4">
                <div className="w-[46px] h-[46px] rounded-[12px] flex items-center justify-center shrink-0"
                  style={{ background: `${oc.icon}20`, border: `1px solid ${oc.border}` }}>
                  {outcome === 'PASS' && <Check size={22} style={{ color: oc.icon }} />}
                  {outcome === 'FAIL' && <X size={22} style={{ color: oc.icon }} />}
                  {outcome === 'REFER' && <AlertTriangle size={22} style={{ color: oc.icon }} />}
                </div>
                <div>
                  <span className="text-[19px] font-extrabold" style={{ color: oc.text }}>{outcome}</span>
                  {entityId && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-[#5b6b85]">entityId</span>
                      <span className="text-[10px] font-mono text-[#94a3b8]">{entityId}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <RawJsonCollapsible data={fullResult} />
          </div>
        )}

        {!hostedUrl && !workflowResult && (
          <div
            className="flex flex-col items-center justify-center py-12 rounded-[14px]"
            style={{ border: '2px dashed #233149', background: '#0d1626' }}
          >
            <SectionLabel>Hosted SDK Workspace</SectionLabel>
            <p className="text-[11px] text-[#3f4f6b] mt-1.5 text-center max-w-xs">
              Fill in the form and click "Generate Hosted URL". The user completes verification inside the iframe.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
